/**
 * Guest → host stream measurement contract.
 *
 * The guest reports what it *measured*; the host decides what it *means*. This replaces the
 * older `congestion` message, in which the guest classified its own link against absolute RTT
 * thresholds while the host classified the same link against queueing above a per-peer RTT
 * floor. Two ladders cannot agree, and the guest's absolute one labelled every cellular link
 * congested forever.
 *
 * Every field is optional and MUST be omitted rather than zero-filled when a poll could not
 * sample it. A fabricated 0 is indistinguishable at the host from a genuine reading, and a
 * zero-pinned RTT floor reads a healthy 30–99 ms link as a standing queue.
 */

import type { FrameDecodeHint } from './frameLatencyTracker';
import type { StreamRecoveryEvidence } from './streamRecovery/contracts';
import { isStreamRecoveryEvidence, hasValidFrameObservation, withoutInvalidFrameObservation } from './streamRecovery/validators';
import { isDecodeLagEpisode, type DecodeLagEpisode } from './decodeLagContracts';
import type { GuestBrowserEngine } from './adverseNetworkContract';
import type { GuestLatencyCapability } from './guestBrowserCapability';
import { isFramePipelineSummary, type FramePipelineSummary } from './framePipeline/framePipelineContracts';
import { isEncodedFrameSummary, type EncodedFrameSummary } from './framePipeline/encodedFrameSizeTracker';
import { isReceiverHintReadback, type ReceiverHintReadback } from './receiverHints/receiverHintContracts';
import {
  isPresentationSummary,
  isReceiverCounterSummary,
  isStatKeyInventory,
  type PresentationSummary,
  type ReceiverCounterSummary,
  type StatKeyInventory,
} from './streamQuality/streamQualityContracts';

export const CLIENT_STREAM_STATS_MESSAGE_TYPE = 'clientStats' as const;

/**
 * Guest send cadence. Matches the host's own stats poll (PeerConnectionManager.STATS_POLL_MS)
 * so both sides of the shared ladder tick at the same rate.
 */
export const CLIENT_STREAM_STATS_INTERVAL_MS = 1_000;

/**
 * How long the host waits without a `clientStats` from a peer before it treats guest-sourced
 * evidence as stale: ≈5 send ticks (also ≈5 host polls). Measured in host-tick time and armed
 * only once that peer's first `clientStats` has arrived — the host owns both the arming and
 * the enforcement; this is just the number both sides reference.
 */
export const CLIENT_STATS_STALE_AFTER_MS = 5_000;

export type ClientStreamStatsPayload = {
  /** Versioned, bounded recovery telemetry; absent or discarded as a whole when unsupported. */
  recoveryEvidence?: StreamRecoveryEvidence;
  /**
   * `Date.now()` on the guest when this poll sampled.
   *
   * Never compare directly to a host timestamp: guest and host clocks may differ. A trusted
   * clock calibration may map this into host-time bounds to qualify freshness. Receipt cadence
   * and seq alone cannot certify absolute freshness of a uniformly delayed report stream.
   */
  sampledAtEpochMs?: number;
  /**
   * Monotonic send counter for this connection: 1, 2, 3, ... in send order,
   * incremented only on DELIVERED sends, reset to 0 on reconnect. Absent on
   * legacy guests. A gap between consecutive values the host receives is
   * evidence of in-transit loss (at-most-once channel), not guest silence.
   */
  seq?: number;
  /** candidate-pair currentRoundTripTime × 1000. Omitted when no succeeded pair was sampled. */
  rttMs?: number;
  /** The guest's own min-filtered RTT floor. Omitted while the floor is untrusted. */
  rttFloorMs?: number;
  /** max(0, rttMs − rttFloorMs). Omitted while the floor is untrusted. */
  standingQueueMs?: number;
  /** Inbound loss over the last sample interval, percent (0–100). */
  packetsLostPct?: number;
  /** jitterBufferDelay / jitterBufferEmittedCount × 1000. */
  jbDelayMs?: number;
  /** Growth of jbDelayMs above its recent window minimum, ms. */
  playoutLatencyTrendMs?: number;
  /** framesReceived − framesDecoded, as a delta over the sample interval. */
  decodeBacklogFrames?: number;
  /**
   * The decoder's standing depth: framesReceived − framesDecoded − framesDropped, ABSOLUTE.
   * The frames a player is waiting behind right now. Omitted when the engine does not expose
   * framesReceived (Safari). Observability + decoder-backup classification; the ladder reads
   * the growth-based `playoutLatencyTrendMs` / `decodeBacklogFrames`, not this.
   */
  standingBacklogFrames?: number;
  /** inbound-rtp framesPerSecond. Omitted when the engine did not report it; a measured 0 is sent. */
  decodeFps?: number;
  /**
   * Video inbound bitrate over the sample interval, in bits per second.
   * Omitted before a baseline exists or when bytesReceived is unavailable.
   * A measured zero is sent as 0.
   */
  videoBitrateBps?: number;
  /**
   * Increase in inbound-rtp framesReceived over the sample interval.
   * Omitted when the engine cannot expose framesReceived.
   * A measured zero is sent as 0.
   */
  receiveFramesDelta?: number;
  /**
   * Wall time a frame spent inside the decoder this interval, ms (Δ totalDecodeTime / Δ
   * framesDecoded). Every engine exposes it, including Safari, which cannot count frames
   * queued for decode: `decodeFps × decodeMsPerFrame / 1000` is the frames resident in the
   * decoder. Observability + decoder-backup classification; never a ladder input.
   */
  decodeMsPerFrame?: number;
  /** freezeCount increase since the previous sample. */
  freezeDelta?: number;
  /**
   * Frame decode hints for host-side end-to-end latency correlation: the RTP timestamps
   * of the most recently decoded frames plus the guest's local decode wall time for each.
   * The host matches each timestamp to its recorded send time to compute glass-to-glass
   * latency. Omitted when no decode hints were captured (no rVFC on this engine yet).
   */
  frameDecodeHints?: FrameDecodeHint[];
  /**
   * The guest's decode-lag episode (sustained decode-side backlog past the shared dwell).
   * Optional and omitted when the decoder is keeping up; the host reads it to trigger the
   * 360p downgrade / fps ladder / latency-first correction. Backward-compatible: older
   * hosts ignore the field.
   */
  decodeLag?: DecodeLagEpisode;
  /**
   * Audio inbound bitrate (bps), observability-only. Omitted when unmeasured. Audio is never
   * an adaptation input — it exists so the unified health record covers all four streams.
   */
  audioBitrateBps?: number;
  /** Audio inbound loss over the last sample interval, percent (0–100). Observability-only. */
  audioPacketsLostPct?: number;
  /** Audio level (0–1) from the inbound audio track. Observability-only. */
  audioLevel?: number;
  /**
   * The REAL inbound-rtp jitter-buffer delay this interval (Δ jitterBufferDelay / Δ
   * jitterBufferEmittedCount × 1000), on every engine that exposes the counters — Android
   * included. Never a synthetic: `jbDelayMs` above was replaced on Android by a
   * backlog-derived estimate, and this field exists so the host can see the buffer itself.
   */
  jitterBufferDelayMs?: number;
  /** inbound-rtp jitterBufferTargetDelay / jitterBufferEmittedCount × 1000, this interval. */
  jitterBufferTargetDelayMs?: number;
  /** inbound-rtp jitterBufferMinimumDelay / jitterBufferEmittedCount × 1000, this interval. */
  jitterBufferMinimumDelayMs?: number;
  /**
   * Video inbound-rtp `jitter` (RFC 3550 interarrival jitter), ms. This is the network's own
   * account of packet-timing variation, independent of the jitter buffer that reacts to it: a
   * target far above this number is the receiver's estimator, not the link.
   */
  jitterMs?: number;
  /** Audio inbound-rtp jitterBufferDelay / jitterBufferEmittedCount × 1000, this interval (NetEq). */
  audioJitterBufferDelayMs?: number;
  /** Audio inbound-rtp jitterBufferTargetDelay / jitterBufferEmittedCount × 1000, this interval. */
  audioJitterBufferTargetDelayMs?: number;
  /**
   * The encoded video frames that arrived this interval, sized. The receiver's jitter
   * estimator scales `(max_frame_size - avg_frame_size)` by a learned channel slope, so the
   * spread between these two is the input that a standing target on a clean link is made of.
   * Omitted on engines without a receive-side encoded transform.
   */
  encodedFrames?: EncodedFrameSummary;
  /** What the video receiver reports its low-latency hints to be. Omitted until a receiver is attached. */
  receiverHint?: ReceiverHintReadback;
  /** What the audio receiver reports its low-latency hints to be. Omitted until that receiver is pinned. */
  audioReceiverHint?: ReceiverHintReadback;
  /**
   * inbound-rtp decoderImplementation, when the engine names it. Chromium hides this (and
   * powerEfficientDecoder) from pages that hold no camera/microphone permission, so on the
   * guest it is usually absent; `codecMimeType` is the codec signal that is always exposed.
   */
  decoderImplementation?: string;
  /** inbound-rtp powerEfficientDecoder, when the engine reports it (same permission gate). */
  powerEfficientDecoder?: boolean;
  /**
   * The codec the guest is actually decoding, from the `codec` stat the video inbound-rtp
   * points at (e.g. "video/H264", "video/VP9", "video/AV1"). Not permission-gated. This is the
   * field that separates a jitter-buffer target inflated by keyframe size on one codec from
   * the same stream decoding cleanly on another.
   */
  codecMimeType?: string;
  /**
   * The guest's engine, so the host can key engine-aware policy (signal availability, lever
   * order) on the guest that is actually connected rather than guessing.
   */
  guestEngine?: GuestBrowserEngine;
  /** inbound-rtp frameWidth / frameHeight of the most recently decoded frame. */
  frameWidth?: number;
  frameHeight?: number;
  /** Per-tick reduction of rVFC frame metadata. Omitted when no frame carried timing this tick. */
  framePipeline?: FramePipelineSummary;
  /** Which rVFC metadata keys this engine populates. Sent once per session, then omitted. */
  latencyCapability?: GuestLatencyCapability;
  /** What reached the glass this interval (presented fps, stalls, dropped frames). Observability only. */
  presentation?: PresentationSummary;
  /** Video inbound-rtp counter deltas as the engine reports them, bypassing the capability matrix. */
  receiverCounters?: ReceiverCounterSummary;
  /** The inbound-rtp stat keys this engine reports. Sent once per connection, then omitted. */
  statKeys?: StatKeyInventory;
};

export type ClientStreamStatsMessage = {
  type: typeof CLIENT_STREAM_STATS_MESSAGE_TYPE;
} & ClientStreamStatsPayload;

/**
 * Field names validated as optional finite numbers by the guard below. The `as const
 * satisfies` form is load-bearing for the lock underneath: a plain
 * `ReadonlyArray<keyof …>` annotation widens the tuple to `keyof ClientStreamStatsPayload`,
 * and an Exclude against that whole key set would erase every name instead of only the
 * registered ones.
 */
const NUMERIC_FIELDS = [
  'sampledAtEpochMs',
  'rttMs',
  'rttFloorMs',
  'standingQueueMs',
  'packetsLostPct',
  'jbDelayMs',
  'playoutLatencyTrendMs',
  'decodeBacklogFrames',
  'standingBacklogFrames',
  'decodeFps',
  'videoBitrateBps',
  'receiveFramesDelta',
  'decodeMsPerFrame',
  'freezeDelta',
  'audioBitrateBps',
  'audioPacketsLostPct',
  'audioLevel',
  'jitterBufferDelayMs',
  'jitterBufferTargetDelayMs',
  'jitterBufferMinimumDelayMs',
  'jitterMs',
  'audioJitterBufferDelayMs',
  'audioJitterBufferTargetDelayMs',
  'frameWidth',
  'frameHeight',
  'seq',
] as const satisfies ReadonlyArray<keyof ClientStreamStatsPayload>;

/**
 * Compile-time lock: every optional-number field of the payload must appear in
 * `NUMERIC_FIELDS` above. `-?` is load-bearing: leaving the optionality marker on the mapped
 * property union-adds `undefined` into the indexed access and swallows the `Exclude`, and a
 * plain `const _x: T[] = []` never forces evaluation — the generic-call constraint is what
 * makes an uncovered field a build error naming it. The mapped condition matches only
 * primitive optional numbers, so object-typed fields (`decodeLag`, `presentation`,
 * `receiverHint`, `audioReceiverHint`, `framePipeline`, `encodedFrames`, `statKeys`, `receiverCounters`,
 * `latencyCapability`) never enter this set.
 */
type UncoveredNumericField = Exclude<
  {
    [K in keyof ClientStreamStatsPayload]-?: ClientStreamStatsPayload[K] extends number | undefined
      ? K
      : never;
  }[keyof ClientStreamStatsPayload],
  (typeof NUMERIC_FIELDS)[number]
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _assertNoUncoveredNumericFields<T extends never>(): void {}
_assertNoUncoveredNumericFields<UncoveredNumericField>();

const LATENCY_CAPABILITY_FLAGS: ReadonlyArray<keyof GuestLatencyCapability> = [
  'hasRtpTimestamp',
  'hasCaptureTime',
  'hasReceiveTime',
  'hasProcessingDuration',
];

function isGuestLatencyCapability(value: unknown): value is GuestLatencyCapability {
  if (value === null || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return LATENCY_CAPABILITY_FLAGS.every((flag) => typeof c[flag] === 'boolean');
}

/**
 * Structural guard for the wire message.
 *
 * A payload carrying only `type` is valid: a poll early in a connection legitimately has
 * nothing to report yet, and rejecting it would make the host treat a starting stream as a
 * malformed one. Present fields must be finite numbers — `NaN`/`Infinity` would propagate
 * into threshold comparisons and silently evaluate false.
 */
export function isClientStreamStatsMessage(value: unknown): value is ClientStreamStatsMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg['type'] !== CLIENT_STREAM_STATS_MESSAGE_TYPE) return false;

  for (const field of NUMERIC_FIELDS) {
    const raw = msg[field];
    if (raw === undefined) continue;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return false;
  }
    // frameDecodeHints is the only non-scalar field; validate its shape when present.
    const hints = msg['frameDecodeHints'];
    if (hints !== undefined) {
      if (!Array.isArray(hints) || hints.length > 120) return false;
      for (const hint of hints) {
        if (hint === null || typeof hint !== 'object') return false;
        const h = hint as Record<string, unknown>;
        if (!hasValidFrameObservation(h)) return false;
        if (typeof h['rtpTimestamp'] !== 'number' || !Number.isFinite(h['rtpTimestamp'])) {
          return false;
        }
        if (typeof h['decodeWallMs'] !== 'number' || !Number.isFinite(h['decodeWallMs'])) {
          return false;
        }
      }
    }


  if (msg['recoveryEvidence'] !== undefined && !isStreamRecoveryEvidence(msg['recoveryEvidence'])) return false;

  // decodeLag is optional; validate its shape when present.
  const decodeLag = msg['decodeLag'];
  if (decodeLag !== undefined && !isDecodeLagEpisode(decodeLag)) return false;

  // Diagnostic objects and scalars added for the standing-latency work; each optional.
  if (msg['receiverHint'] !== undefined && !isReceiverHintReadback(msg['receiverHint'])) return false;
  if (msg['audioReceiverHint'] !== undefined && !isReceiverHintReadback(msg['audioReceiverHint'])) return false;
  if (msg['framePipeline'] !== undefined && !isFramePipelineSummary(msg['framePipeline'])) return false;
  if (msg['encodedFrames'] !== undefined && !isEncodedFrameSummary(msg['encodedFrames'])) return false;
  if (msg['latencyCapability'] !== undefined && !isGuestLatencyCapability(msg['latencyCapability'])) return false;
  if (msg['presentation'] !== undefined && !isPresentationSummary(msg['presentation'])) return false;
  if (msg['receiverCounters'] !== undefined && !isReceiverCounterSummary(msg['receiverCounters'])) return false;
  if (msg['statKeys'] !== undefined && !isStatKeyInventory(msg['statKeys'])) return false;
  if (msg['decoderImplementation'] !== undefined && typeof msg['decoderImplementation'] !== 'string') return false;
  if (msg['powerEfficientDecoder'] !== undefined && typeof msg['powerEfficientDecoder'] !== 'boolean') return false;
  if (msg['codecMimeType'] !== undefined && typeof msg['codecMimeType'] !== 'string') return false;
  if (msg['guestEngine'] !== undefined && !isGuestBrowserEngine(msg['guestEngine'])) return false;

  return true;
}

const GUEST_BROWSER_ENGINES: ReadonlyArray<GuestBrowserEngine> = ['safari-ios', 'android-chrome', 'desktop-chrome'];

function isGuestBrowserEngine(value: unknown): value is GuestBrowserEngine {
  return typeof value === 'string' && (GUEST_BROWSER_ENGINES as ReadonlyArray<string>).includes(value);
}

/** Decode optional extensions without mutating a caller's original wire message. */
export function parseClientStreamStatsMessage(value: unknown): ClientStreamStatsMessage | undefined {
  if (isClientStreamStatsMessage(value)) return value;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const cleaned = { ...value } as Record<string, unknown>;
  if (!isStreamRecoveryEvidence(cleaned['recoveryEvidence'])) delete cleaned['recoveryEvidence'];
  const hints = cleaned['frameDecodeHints'];
  if (Array.isArray(hints) && hints.length <= 120) {
    cleaned['frameDecodeHints'] = hints.map(hint => hint && typeof hint === 'object' && !Array.isArray(hint)
      ? withoutInvalidFrameObservation(hint as Record<string, unknown>) : hint);
  }
  return isClientStreamStatsMessage(cleaned) ? cleaned : undefined;
}
